// クラシック版の柄の一覧と柄の選択（段階 C の2 の 2c。2026-10-25 に chibi.js・画面ファイル gen2.js から描き直した。見た目・動きは前と同じ）。
// 画面の系統（chibiConfig.SCREEN）で前の画面ファイルの違いを出す: 柄の一覧 setPatternList はエンジンのまま／第2世代（前の gen2.js。ピッカーは正方形・残りが一覧、スマホでも縦一列）。
// 柄のある第1世代（mono、PATTERN_IN_COLOR）の setPatternList・selPattern・clearPattern・setPattern は gen1pattern.ts の版（main.ts があとから上書きで置く）
import type { Category } from '../types.js';
import { chibi, common } from './engine.js';
import { appendSvg, bindData, onClick, svgEl } from './dom.js';
import { screen } from './colors.js';
import * as fx from './fx.js';
import type { Selection } from './selection.js';

interface Cursor {
  baseCsize: number;
  baseX: number;
  baseY: number;
  baseW: number;
  baseH: number;
  stroke: string;
  strokewidth: number;
}
interface PatternEngine {
  isMobile: boolean;
  DIV_F_PATTERN: string;
  DIV_F_PATTERN_SC: string;
  DIV_F_PICKPATTERN: string;
  SVG_PATTERN: string;
  SVG_SAVE_DEF: string;
  ATTR_CPATTERN: string;
  ATTR_EDITFLOATBUTTON: string;
  ATTR_LAYER: string;
  PARTS_BG: string;
  SIZE_PATTERN: number;
  ADJUST_PATTERN: number;
  COLOR_CURSOR: string;
  svgPattern: Selection;
  svgPickPattern: Selection;
  svgMake: Selection;
  selLayer: Selection;
  cursorPattern: Cursor[];
  enableEditPattern: boolean;
  patternList: ArrayLike<Element>;
  selCategory: Category;
  groupCategory: Category[];
  btnRevColor: unknown;
  selPattern(index: number): void;
  clearPattern(): void;
  setPattern(id: string): void;
  coreOps(): { pattern(sel: Category, group: Category[], id: string): void; clearPattern(sel: Category, group: Category[]): void };
}
const E = (): PatternEngine => chibi() as unknown as PatternEngine;
type Common = { changeColor(svg: Selection, cat: Category, layerKey: string, color: string, sel: number, org?: unknown): void; afterChangeColor(): void };
const C = (): Common => common() as unknown as Common;

/**
 * 柄の一覧（左の列）と、色の入れかえのボタン（ピッカー欄）を作る。aPatternList: 柄の SVG の g の並び、aW・aH: 柄の丸 1 つ分の大きさ。
 * 各柄は保存用の SVG の defs に <pattern id="cpattern<番号>"> として置き、丸はそれで塗る（先頭は「柄なし」の白丸）
 */
export function setPatternList(aPatternList: ArrayLike<Element>, aW: number, aH: number): void {
  const e = E(), gen2 = screen() === 'gen2';
  // 柄の領域の高さ（エンジンのまま: 一覧もピッカーも領域いっぱい。第2世代: ピッカーは正方形、残りが一覧）
  if (gen2) {
    fx.setHeight(e.DIV_F_PICKPATTERN, fx.width(e.DIV_F_PICKPATTERN));
    fx.setHeight(e.DIV_F_PATTERN_SC, fx.height(e.DIV_F_PATTERN) - fx.height(e.DIV_F_PICKPATTERN));
  } else {
    fx.setHeight(e.DIV_F_PATTERN_SC, fx.height(e.DIV_F_PATTERN));
    fx.setHeight(e.DIV_F_PICKPATTERN, fx.height(e.DIV_F_PATTERN));
  }
  const fwidth = aH / 12;
  const fmargin = aH / 34; // 柄ボタンの間
  const cCircle = aH / 2.2; // 柄ボタンの大きさ
  const cSize = aH / 2.5; // カーソルの枠の大きさ
  // 色の入れかえのボタン（ピッカー欄。注意: svg の id に div と同じ id を渡しているので、div と svg で id が重なる。前と同じ）
  e.svgPickPattern = fx.d3Select(appendSvg(e.DIV_F_PICKPATTERN, e.DIV_F_PICKPATTERN, aW, aH));
  fx.floatButton({
    LAYER: e.svgPickPattern,
    CLASS: e.ATTR_EDITFLOATBUTTON,
    VISIBLE: true,
    REAR: 'circle',
    DATA: [{ BTN: e.btnRevColor, POS: { X: aW * 0.09, Y: aH * 0.05 }, SIZE: { W: aW * 0.9, H: aH * 0.9 }, SCALE: 1, COLOR: ['transparent', 'transparent'] }],
  });
  // 柄の丸の塗り用に、各柄を保存用の SVG の defs に置く
  const saveDef = document.querySelector('#' + e.SVG_SAVE_DEF);
  const patternIds = ['000'];
  for (let i = 0; i < aPatternList.length; i++) {
    const src = aPatternList[i];
    if (!(src instanceof Node)) continue;
    patternIds.push(src.id);
    if (!saveDef) continue;
    const pattern = svgEl('pattern', { id: e.ATTR_CPATTERN + src.id, patternUnits: 'userSpaceOnUse', x: 0, y: 0, width: e.SIZE_PATTERN, height: e.SIZE_PATTERN });
    const g = svgEl('g', { transform: 'translate(' + e.ADJUST_PATTERN + ',' + e.ADJUST_PATTERN + '), scale(1,1)' });
    g.appendChild(src.cloneNode(true));
    pattern.appendChild(g);
    saveDef.appendChild(pattern);
  }
  // 柄の丸（エンジンのままのスマホは横に並ぶので、svg の幅と高さを入れ替える）
  const svg = appendSvg(e.DIV_F_PATTERN, e.SVG_PATTERN, aW, (patternIds.length + 4) * aH);
  if (svg && !gen2 && e.isMobile) {
    const w0 = svg.getAttribute('width')!, h0 = svg.getAttribute('height')!;
    svg.setAttribute('width', h0);
    svg.setAttribute('height', w0);
  }
  e.svgPattern = fx.d3Select(svg);
  if (svg) svg.setAttribute('display', 'none');
  patternIds.forEach((d, i) => {
    if (!svg) return;
    const circle = svgEl('circle', { id: e.SVG_PATTERN + d });
    const along = i * (aH + fmargin) + fwidth + cCircle, across = fwidth + cCircle;
    // 属性の並びも前と同じに（エンジンのままは cy → cx、第2世代は cx → cy）
    if (gen2) {
      circle.setAttribute('cx', String(across));
      circle.setAttribute('cy', String(along));
    } else {
      circle.setAttribute('cy', String(e.isMobile ? across : along));
      circle.setAttribute('cx', String(e.isMobile ? along : across));
    }
    circle.setAttribute('r', String(cCircle));
    circle.setAttribute('style', 'stroke: #C7D9E8; stroke-width: 2; fill:' + (d === '000' ? 'White' : 'url(#' + e.ATTR_CPATTERN + d + ')'));
    circle.style.setProperty('cursor', 'pointer');
    onClick(circle, () => {
      if (E().enableEditPattern) E().selPattern(i);
    });
    bindData(circle, d);
    svg.appendChild(circle);
  });
  // 選んでいる柄のカーソル
  e.cursorPattern = [0, 1].map(() => ({ baseCsize: cSize, baseX: fwidth + cCircle, baseY: fwidth + cCircle, baseW: aW, baseH: aH + fmargin, stroke: e.COLOR_CURSOR, strokewidth: fwidth }));
  e.cursorPattern.forEach((dd) => {
    if (!svg) return;
    svg.appendChild(svgEl('circle', { class: 'SelColor', cx: dd.baseX, cy: dd.baseY, r: dd.baseCsize, fill: 'none', stroke: dd.stroke, 'stroke-width': dd.strokewidth }));
  });
  // スクロールバー（PC）
  if (!e.isMobile) fx.scrollbarY(e.DIV_F_PATTERN_SC);
}

/** 柄の一覧で柄を選ぶ（aPtnIndex: 一覧の位置。0 は「柄なし」、1 以降は patternList[aPtnIndex - 1]） */
export function selPattern(aPtnIndex: number): void {
  const e = E();
  if (e.patternList.length == 0 || aPtnIndex < 0) return;
  // 柄のカーソル
  const svg = e.svgPattern.node();
  fx.bounceCursor(
    svg ? Array.from(svg.querySelectorAll('circle.SelColor')) : [],
    (i) => ({ cy: e.cursorPattern[i].baseY + aPtnIndex * e.cursorPattern[i].baseH, r: e.cursorPattern[i].baseCsize * (i * 1.5), stroke: i == 0 ? '#882244' : '#00FFFF' }),
    (i) => ({ r: e.cursorPattern[i].baseCsize, stroke: e.COLOR_CURSOR }),
  );
  if (aPtnIndex == 0) {
    // 柄なし: 柄を外し、背景以外はレイヤーの色（paramCol）で塗り直す（カテゴリ全体を塗る changeColor をレイヤーの数だけ繰り返している。前と同じ）
    E().clearPattern();
    E().groupCategory.forEach((category) => {
      if (category.LAYER[0] == E().PARTS_BG) return;
      const pcol = E().svgMake.select('#' + E().ATTR_LAYER + category.LAYER[0]).attr('paramCol');
      category.LAYER.forEach(() => {
        C().changeColor(E().svgMake, category, E().ATTR_LAYER, pcol, 0, undefined);
      });
    });
  } else {
    // 柄あり: 今と違う柄なら付け替える
    const pattern = E().patternList[aPtnIndex - 1];
    if (E().selLayer.attr('paramPtn') != pattern.id) E().setPattern(pattern.id);
  }
}

/** 選んでいるカテゴリのグループから柄を外す（値を書いて元の塗りに戻すのは中身。段階 B4、core/classic.ts の clearPattern） */
export function clearPattern(): void {
  E().coreOps().clearPattern(E().selCategory, E().groupCategory);
  C().afterChangeColor();
}
/**
 * 選んでいるカテゴリのグループに柄を付ける（aPtnId: 柄番号）。柄を作って貼るのは中身（段階 B4、core/classic.ts の pattern）。
 * 前のエンジンはパーツの要素を使い回していて、色を替えたあとの後処理で塗った色を「元の色」として覚えていたので、柄・色の入れかえで元に戻してもその色だった。
 * 中身は新しく作った要素なので、ここで後処理を掛け直す（2026-10-24、段階 B5。kawaii2 のインナーの線）
 */
export function setPattern(aPtnId: string): void {
  E().coreOps().pattern(E().selCategory, E().groupCategory, aPtnId);
  C().afterChangeColor();
}
